import { useRef, useEffect, useCallback } from "react";
import type { Message, PendingApproval } from "../../shared/types";
import {
  setConversationId as saveConversationId,
  getStoredMessages,
  setStoredMessages,
  getConversationId,
  getPendingApprovals,
} from "../utils/storage";

type StoredPendingApproval = PendingApproval & { error?: string };

interface UseCrossTabSyncParams {
  agentId: string;
  messagesRef: React.MutableRefObject<Message[]>;
  messages: Message[];
  pendingApprovals: StoredPendingApproval[];
  conversationId: string | null;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  setConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  setPendingApprovals: React.Dispatch<
    React.SetStateAction<StoredPendingApproval[]>
  >;
  onClear?: () => void;
}

interface UseCrossTabSyncResult {
  handleThreadId: (threadId: string) => void;
  conversationIdRef: React.MutableRefObject<string | null>;
  reset: () => void;
}

function hasChanged<T>(prev: T, next: T): boolean {
  return JSON.stringify(prev) !== JSON.stringify(next);
}

export function useCrossTabSync({
  agentId,
  messagesRef,
  messages,
  pendingApprovals,
  conversationId,
  setMessages,
  setConversationId,
  setPendingApprovals,
  onClear,
}: UseCrossTabSyncParams): UseCrossTabSyncResult {
  const broadcastRef = useRef<BroadcastChannel | null>(null);
  const broadcastDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isHydrating = useRef(true);
  const isApplyingBroadcastApprovals = useRef(false);
  const isApplyingBroadcastMessages = useRef(false);
  const conversationIdRef = useRef(conversationId);

  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  useEffect(() => {
    isHydrating.current = false;
  }, [agentId]);

  // conversationId intentionally omitted from deps — use conversationIdRef.current to avoid stale closure re-broadcasts
  useEffect(() => {
    const currentConversationId = conversationIdRef.current;
    if (!currentConversationId || messages.length === 0) return;

    setStoredMessages(agentId, messages);

    if (isHydrating.current || !broadcastRef.current) return;

    if (isApplyingBroadcastMessages.current) {
      isApplyingBroadcastMessages.current = false;
      return;
    }

    broadcastRef.current.postMessage({
      type: "messages:update",
      agentId,
      conversationId: currentConversationId,
      messages,
    });
  }, [messages, agentId]);

  useEffect(() => {
    if (
      isHydrating.current ||
      isApplyingBroadcastApprovals.current ||
      !broadcastRef.current ||
      !conversationIdRef.current
    ) {
      if (isApplyingBroadcastApprovals.current)
        isApplyingBroadcastApprovals.current = false;
      return;
    }

    const conversationIdSnapshot = conversationIdRef.current;

    clearTimeout(broadcastDebounceRef.current ?? undefined);
    broadcastDebounceRef.current = setTimeout(() => {
      if (!broadcastRef.current) return;
      broadcastRef.current.postMessage({
        type: "pendingApprovals:update",
        agentId,
        conversationId: conversationIdSnapshot,
        pendingApprovals,
      });
    }, 50);

    return () => {
      clearTimeout(broadcastDebounceRef.current ?? undefined);
    };
  }, [pendingApprovals, agentId]);

  useEffect(() => {
    if (typeof window === "undefined" || !("BroadcastChannel" in window)) {
      return;
    }

    const channel = new BroadcastChannel(
      `resident-agent:${agentId}:conversation`,
    );
    broadcastRef.current = channel;

    function handleClear() {
      setMessages([]);
      setConversationId(null);
      onClear?.();
    }

    function handleConversationIdUpdate(incomingId: string) {
      if (
        !conversationIdRef.current ||
        conversationIdRef.current !== incomingId
      ) {
        setConversationId(incomingId);
        saveConversationId(agentId, incomingId);
      }

      const stored = getStoredMessages(agentId);
      if (stored && stored.length > 0) {
        setMessages((prev) => {
          if (!hasChanged(prev, stored)) return prev;
          isApplyingBroadcastMessages.current = true;
          return stored;
        });
      }

      const storedApprovals = getPendingApprovals(agentId);
      setPendingApprovals((prev) => {
        if (!hasChanged(prev, storedApprovals)) return prev;
        isApplyingBroadcastApprovals.current = true;
        return storedApprovals;
      });
    }

    function handleMessagesUpdate(
      incomingId: string,
      incomingMessages: Message[],
    ) {
      if (incomingId !== conversationIdRef.current) return;
      setMessages((prev) =>
        hasChanged(prev, incomingMessages) ? incomingMessages : prev,
      );
    }

    function handlePendingApprovalsUpdate(
      incomingId: string | undefined,
      incomingApprovals: StoredPendingApproval[],
    ) {
      // Accept when: sender omitted conversationId, this tab has none yet, or IDs match.
      // Prevents applying approvals from a different conversation.
      const conversationMatches =
        !incomingId ||
        !conversationIdRef.current ||
        incomingId === conversationIdRef.current;

      if (!conversationMatches) return;

      isApplyingBroadcastApprovals.current = true;
      setPendingApprovals(incomingApprovals);
    }

    channel.onmessage = (event) => {
      const {
        type,
        agentId: incomingAgentId,
        messages: incomingMessages,
        conversationId: incomingId,
        pendingApprovals: incomingApprovals,
      } = event.data || {};

      if (incomingAgentId !== agentId) {
        console.warn(
          `[useCrossTabSync] Ignoring broadcast from different agent: ${incomingAgentId}`,
        );
        return;
      }

      if (type === "messages:clear") {
        handleClear();
        return;
      }

      if (type === "conversationId:update" && incomingId) {
        handleConversationIdUpdate(incomingId);
        return;
      }

      if (type === "messages:update" && Array.isArray(incomingMessages)) {
        handleMessagesUpdate(incomingId, incomingMessages);
      }

      if (
        type === "pendingApprovals:update" &&
        Array.isArray(incomingApprovals)
      ) {
        handlePendingApprovalsUpdate(
          incomingId,
          incomingApprovals as StoredPendingApproval[],
        );
      }
    };

    const pollInterval = setInterval(() => {
      const storedId = getConversationId(agentId);
      if (storedId && storedId !== conversationIdRef.current) {
        setConversationId(storedId);
      }

      const storedMessages = getStoredMessages(agentId);
      if (storedMessages && storedMessages.length > 0) {
        setMessages((prev) =>
          hasChanged(prev, storedMessages) ? storedMessages : prev,
        );
      }

      if (conversationIdRef.current) {
        const storedApprovals = getPendingApprovals(agentId);
        setPendingApprovals((prev) =>
          hasChanged(prev, storedApprovals) ? storedApprovals : prev,
        );
      }
    }, 5000);

    return () => {
      clearInterval(pollInterval);
      channel.close();
      broadcastRef.current = null;
    };
  }, [agentId]);

  const handleThreadId = useCallback(
    (threadId: string) => {
      if (conversationIdRef.current === threadId) return;

      conversationIdRef.current = threadId;
      setConversationId(threadId);
      saveConversationId(agentId, threadId);

      const currentMessages = messagesRef.current;
      if (currentMessages.length > 0) {
        setStoredMessages(agentId, currentMessages);
      }

      if (broadcastRef.current) {
        broadcastRef.current.postMessage({
          type: "conversationId:update",
          agentId,
          conversationId: threadId,
        });
        if (currentMessages.length > 0) {
          broadcastRef.current.postMessage({
            type: "messages:update",
            agentId,
            conversationId: threadId,
            messages: currentMessages,
          });
        }
      }
    },
    [agentId],
  );

  const reset = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setPendingApprovals([]);
    onClear?.();

    if (broadcastRef.current) {
      broadcastRef.current.postMessage({
        type: "messages:clear",
        agentId,
      });
    }
  }, [agentId, setMessages, setConversationId, setPendingApprovals, onClear]);

  return { handleThreadId, conversationIdRef, reset };
}
